import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import ScrollableDropdown from './ScrollableDropdown';

const AddTeamMembersModal = ({ isOpen, onClose, applicationId, onSuccess }) => {
    const [availableMembers, setAvailableMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [roleFilter, setRoleFilter] = useState('ALL'); // ALL, QA, DEV

    const fetchAvailableMembers = useCallback(async () => {
        try {
            const { data } = await api.get(`/api/applications/${applicationId}/available-members`);
            setAvailableMembers(data.data);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load available members');
        }
    }, [applicationId]);

    useEffect(() => {
        if (isOpen && applicationId) {
            fetchAvailableMembers();
        }
    }, [isOpen, applicationId, fetchAvailableMembers]);

    const filteredMembers = availableMembers.filter(member => {
        // Only include Developers and QAs
        const isEligibleRole = ['DEV', 'QA'].includes(member.roleLabel);
        if (!isEligibleRole) return false;

        const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = roleFilter === 'ALL' || member.roleLabel === roleFilter;
        return matchesSearch && matchesRole;
    });

    const toggleMember = (memberId) => {
        setSelectedMembers(prev =>
            prev.includes(memberId)
                ? prev.filter(id => id !== memberId)
                : [...prev, memberId]
        );
    };

    const handleSelectAll = () => {
        const visibleIds = filteredMembers.map(m => m._id);
        setSelectedMembers(prev => {
            const newSelection = [...new Set([...prev, ...visibleIds])];
            return newSelection;
        });
        toast.success(`Selected all ${visibleIds.length} visible members`);
    };

    const handleClearAll = () => {
        setSelectedMembers([]);
        toast.success('Cleared all selections');
    };

    const handleAddMembers = async () => {
        if (selectedMembers.length === 0) {
            toast.error('Please select at least one member');
            return;
        }

        setLoading(true);
        console.log('Adding team members:', { applicationId, selectedMembers }); // Debug log
        try {
            // Send both formats for maximum compatibility
            const payload = {
                userIds: selectedMembers,
                members: selectedMembers.map(uid => {
                    const member = availableMembers.find(m => m._id === uid);
                    return {
                        userId: uid,
                        role: member?.roleLabel
                    };
                })
            };

            const { data } = await api.post(`/api/applications/${applicationId}/team-members`, payload);

            if (data.success) {
                toast.success(data.message || `Successfully added ${selectedMembers.length} team member(s)`);

                // Safe cleanup sequence to prevent browser message channel closure errors
                setTimeout(() => {
                    setSelectedMembers([]);
                    setSearchTerm('');
                    if (typeof onSuccess === 'function') onSuccess();
                    if (typeof onClose === 'function') onClose();
                }, 100); // Small delay for visual polish and safety
            } else {
                toast.error(data.message || 'Failed to add team members');
            }
        } catch (error) {
            console.error('Add members error:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Failed to add team members';
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200 border border-white/20">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">Add Team Members</h3>
                        <p className="text-[11px] text-gray-400 mt-0.5 font-medium uppercase tracking-wider">Configure Application Access</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex flex-col p-4 gap-4">
                    {/* Available Members Section */}
                    <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex items-center justify-between mb-2 px-1">
                            <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">
                                1. Available Members
                            </h4>
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={handleSelectAll}
                                    disabled={filteredMembers.length === 0}
                                    className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 disabled:text-gray-300 transition-colors uppercase tracking-tight"
                                >
                                    Select All
                                </button>
                                <div className="h-3 w-[1px] bg-gray-200"></div>
                                <button 
                                    onClick={handleClearAll}
                                    disabled={selectedMembers.length === 0}
                                    className="text-[10px] font-bold text-red-500 hover:text-red-700 disabled:text-gray-300 transition-colors uppercase tracking-tight"
                                >
                                    Clear All
                                </button>
                            </div>
                        </div>
                        
                        <div className="flex gap-2 mb-3">
                            <div className="relative flex-1">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-300" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Filter by name or email..."
                                    className="w-full pl-9 pr-4 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-400 text-xs transition-all placeholder:text-gray-300"
                                />
                            </div>
                            <div className="w-32 text-xs">
                                <ScrollableDropdown
                                    value={roleFilter}
                                    onChange={(value) => setRoleFilter(value)}
                                    options={[
                                        { value: 'ALL', label: 'All Roles' },
                                        { value: 'DEV', label: 'Developers' },
                                        { value: 'QA', label: 'Testers' },
                                    ]}
                                    placeholder="All Roles"
                                    buttonHeightClassName="h-[32px]"
                                    maxMenuHeightPx={200}
                                    maxMenuHeightClassName="max-h-48"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto rounded-xl bg-gray-50/50 border border-gray-100 p-1">
                            {filteredMembers.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center py-8 text-center opacity-30">
                                    <MagnifyingGlassIcon className="h-6 w-6 text-gray-400 mb-1" />
                                    <p className="text-[10px] text-gray-500 font-bold uppercase">No candidates available</p>
                                </div>
                            ) : (
                                <div className="space-y-0.5">
                                    {filteredMembers.map((member) => {
                                        const isSelected = selectedMembers.includes(member._id);

                                        return (
                                            <div
                                                key={member._id}
                                                onClick={() => toggleMember(member._id)}
                                                className={`group relative p-2 rounded-lg cursor-pointer transition-all border ${isSelected
                                                    ? 'bg-white border-indigo-200 shadow-sm'
                                                    : 'bg-transparent border-transparent hover:bg-white hover:border-gray-200'
                                                    }`}
                                            >
                                                <div className="flex items-center">
                                                    <div className={`h-4 w-4 rounded border transition-all flex items-center justify-center mr-3 ${isSelected ? 'bg-indigo-600 border-indigo-600 shadow-sm shadow-indigo-200' : 'bg-white border-gray-300 group-hover:border-indigo-400'}`}>
                                                        {isSelected && (
                                                            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[12px] font-bold text-gray-700 truncate">
                                                                {member.name}
                                                            </span>
                                                            <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter ${member.roleLabel === 'QA'
                                                                ? 'bg-purple-50 text-purple-600 border border-purple-100'
                                                                : 'bg-blue-50 text-blue-600 border border-blue-100'
                                                                }`}>
                                                                {member.roleLabel}
                                                            </span>
                                                        </div>
                                                        <p className="text-[10px] text-gray-400 truncate -mt-0.5">{member.email}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Selected Members Section - Fixed Height to prevent layout push */}
                    {selectedMembers.length > 0 && (
                        <div className="flex flex-col h-[110px] min-h-[110px] bg-white rounded-xl border border-dashed border-indigo-200 shadow-inner bg-indigo-50/10">
                             <div className="flex items-center justify-between px-3 py-1.5 bg-indigo-50/50 border-b border-indigo-100/30">
                                <h4 className="text-[9px] font-black text-indigo-400 uppercase tracking-widest flex items-center gap-2">
                                    2. Selected ({selectedMembers.length})
                                </h4>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto p-1.5 space-y-1">
                                {selectedMembers.map(memberId => {
                                    const member = availableMembers.find(m => m._id === memberId);
                                    return (
                                        <div key={memberId} className="flex items-center justify-between bg-white px-2 py-1 rounded-md border border-gray-100 shadow-sm animate-in slide-in-from-bottom-1 duration-200">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[11px] font-bold text-gray-800 truncate">
                                                        {member?.name}
                                                    </span>
                                                    <span className="text-[9px] text-gray-300 truncate">({member?.email})</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => toggleMember(memberId)}
                                                className="ml-2 text-gray-200 hover:text-red-500 p-0.5 hover:bg-red-50 rounded-md transition-all"
                                            >
                                                <XMarkIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-3 border-t border-gray-50 bg-gray-50/50">
                    <div className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                        {selectedMembers.length} member{selectedMembers.length !== 1 ? 's' : ''} queued
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-1.5 text-[11px] font-bold text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddMembers}
                            disabled={loading || selectedMembers.length === 0}
                            className="px-5 py-1.5 bg-indigo-600 text-white rounded-lg text-[11px] font-bold hover:bg-indigo-700 disabled:bg-indigo-100 disabled:text-indigo-300 disabled:cursor-not-allowed transition-all shadow-lg shadow-indigo-500/20 active:scale-95 flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="h-3 w-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Adding...
                                </>
                            ) : (
                                `Confirm Selection (${selectedMembers.length})`
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default AddTeamMembersModal;
